/**
 * Challenge links: a finished player's result travels in the link (`?c=m4-1512-3`), so no
 * backend is needed. The friend sees it only after playing the same day at the same level.
 * Format: 2<level letter e|m><day number>-<total score>-<place named at, 0 = not named>. The leading "2" is the epoch:
 * since the restart (DECISIONS 2026-10-03 "Restart") puzzle numbers began again at #1, so links from before
 * (no epoch, `h` for old Hard) point at puzzles that no longer exist and are ignored.
 * Anyone can edit a link; it's a friendly nudge, not a leaderboard.
 */
import type { Level } from "../puzzles/types";
import { MAX_SCORE_BOUND } from "./scoring";

export interface Challenge {
  level: Level;
  day: number;
  score: number;
  /** "Whose steps?" place it was named at (1–5), 0 if not named. */
  clue: number;
}

/** Hard was merged into Classic before the restart, so it encodes as Classic. */
const L: Record<Level, string> = { easy: "e", medium: "m", hard: "m" };
const BY_LETTER: Record<string, Level> = { e: "easy", m: "medium" };
export const EPOCH = "2";

export const encodeChallenge = (c: Challenge): string => `${EPOCH}${L[c.level]}${c.day}-${c.score}-${c.clue}`;

/** A challenge code from before the restart: well formed but without the epoch. */
export const isOldChallenge = (code: string | null | undefined): boolean => /^[emh]\d{1,4}-\d{1,4}-[0-5]$/.test((code ?? "").trim());

export function decodeChallenge(code: string | null | undefined): Challenge | null {
  const m = /^2([em])(\d{1,4})-(\d{1,4})-([0-5])$/.exec((code ?? "").trim());
  if (!m) return null;
  const c = { level: BY_LETTER[m[1]!]!, day: Number(m[2]), score: Number(m[3]), clue: Number(m[4]) };
  return c.day >= 1 && c.score <= MAX_SCORE_BOUND ? c : null;
}

export const challengeUrl = (origin: string, c: Challenge): string => `${origin}/?c=${encodeChallenge(c)}`;

export type Outcome = "win" | "lose" | "tie";
/** "Not named" ranks after place 5 in the tie-break. */
const NOT_NAMED = 6;
const rank = (clue: number): number => (clue >= 1 && clue <= 5 ? clue : NOT_NAMED);
/**
 * Higher total wins. Equal totals go to whoever named the person at an earlier place in "Whose steps?"
 * ("not named" counts as place 6); still equal is a tie (DECISIONS 2026-10-03). The place is already in the link.
 */
export function outcome(mine: number, theirs: number, myClue = 0, theirClue = 0): Outcome {
  if (mine !== theirs) return mine > theirs ? "win" : "lose";
  const a = rank(myClue);
  const b = rank(theirClue);
  return a < b ? "win" : a > b ? "lose" : "tie";
}
/** Whether an outcome was settled by the tie-break (equal totals, different places). */
export const byTieBreak = (mine: number, theirs: number, myClue = 0, theirClue = 0): boolean => mine === theirs && rank(myClue) !== rank(theirClue);
